import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Icon } from '../Icon';
import type { ColumnKey } from './boardModel';

/**
 * True while a scrollable list has cards below its visible part — drives the bottom fade, so a
 * card cut at the column's foot reads as "more below" rather than as a broken card.
 */
function useMoreBelow<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [more, setMore] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setMore(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(measure);
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
      mo?.disconnect();
    };
  });
  return [ref, more] as const;
}

/**
 * One column: a drop target of its own (so an empty column accepts a card) around a sortable
 * list of cards. The "No workflow label" source column can collapse to a narrow strip that is
 * still a drop target (drop a card there to take it off the plan; it goes to the bottom).
 */
export function BoardColumn({
  column,
  title,
  count,
  hint,
  collapsed,
  onToggle,
  target = false,
  children,
}: {
  column: ColumnKey;
  title: string;
  count: number | null;
  hint?: ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
  /** A card from another column is over this one (highlight the column). */
  target?: boolean;
  children: ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: `col-${column}`, data: { column, collapsed: !!collapsed } });
  const [listRef, moreBelow] = useMoreBelow<HTMLUListElement>();
  const cls = ['board-col', `board-col--${column}`];
  if (collapsed) cls.push('board-col--collapsed');
  if (target) cls.push('board-col--over');
  return (
    <section
      ref={setNodeRef}
      className={cls.join(' ')}
      data-testid={`board-col-${column}`}
      aria-label={`${title} column`}
    >
      <header className="board-col__head">
        {onToggle && (
          <button
            type="button"
            className="mini-btn mini-btn--icon board-col__toggle"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `Show ${title}` : `Hide ${title}`}
            title={collapsed ? `Show ${title}` : `Hide ${title}`}
          >
            <Icon name="chevron" size={14} className={collapsed ? 'req-ico board-chev board-chev--closed' : 'req-ico board-chev'} />
          </button>
        )}
        <h3 className="board-col__title">{title}</h3>
        {count != null && <span className="board-col__count">{count}</span>}
      </header>
      {/* Always a subtitle slot, so every header in a row has the same height. */}
      {!collapsed && <p className="board-col__hint">{hint}</p>}
      {!collapsed && (
        <ul
          ref={listRef}
          className={moreBelow ? 'board-col__list board-col__list--more' : 'board-col__list'}
          data-more-below={moreBelow ? 'true' : undefined}
        >
          {children}
        </ul>
      )}
    </section>
  );
}
